/* ============================================================================
   ATHLO+ — THE BENCH: build a shoe, find the real ones
   ----------------------------------------------------------------------------
   Two things:
     1. A parametric SHOE drawn from the specs — outsole, midsole, plate, upper,
        each its own colour with a legend, so it is obvious which part is which.
     2. The four closest real shoes in the Lab, live, as the sliders move.

   HEEL AND FOREFOOT ARE SEPARATE SLIDERS AND DROP IS AN OUTPUT. A maximalist and
   a minimalist shoe can share an 8mm drop and feel nothing alike, so a drop-only
   control would teach the wrong thing. Do not collapse them.

   THE SHOE IS BUILT FROM ANCHORS ON THE CURVE, not hand-placed bezier control
   points. Control points are not on the curve, which is why three earlier attempts
   produced a blob. UPPER below is a real shoe's side profile as fractions of shoe
   length, joined with Catmull-Rom. Everything — upper, midsole, plate, outsole —
   is anchored to the footbed, so no part can drift away from another.

   NO ANIMATION, deliberately, removed 2026-08-24 at the owner's call. It never
   read as a shoe running, only as a shape rotating, and a wrong-looking animation
   is worse than none. Revisiting it needs a designed shoe silhouette first, not
   more procedural paths; git history holds the gait model if that day comes.
   ========================================================================== */
(function () {
  'use strict';

  var root = document.querySelector('[data-shoesim]');
  if (!root) return;

  // ---- data ---------------------------------------------------------------
  function collect() {
    var byId = {};
    (window.HUB_SHOES || []).forEach(function (s) { byId[s.id] = s; });
    (window.HUB_SHOES_MO || []).forEach(function (s) { byId[s.id] = Object.assign({}, byId[s.id] || {}, s); });
    return Object.keys(byId).map(function (k) { return byId[k]; });
  }
  var lastMatches = [];
  var MATCHABLE = collect().filter(function (s) {
    return typeof s.heel === 'number' && s.heel > 0 &&
           typeof s.fore === 'number' && s.fore > 0 &&
           typeof s.weight === 'number' && s.weight > 0;
  });

  var q = function (sel) { return root.querySelector(sel); };
  var el = {
    heel: q('[data-sim="heel"]'), fore: q('[data-sim="fore"]'), weight: q('[data-sim="weight"]'),
    plate: q('[data-sim="plate"]'), strike: q('[data-sim="strike"]'),
    heelOut: q('[data-out="heel"]'), foreOut: q('[data-out="fore"]'), weightOut: q('[data-out="weight"]'),
    drop: q('[data-out="drop"]'), band: q('[data-out="band"]'), legal: q('[data-out="legal"]'),
    matches: q('[data-out="matches"]'), count: q('[data-out="count"]'),
    share: q('[data-sim-share]'), card: q('[data-sim-card]'),
    compare: q('[data-sim-compare]'),
    svg: q('[data-sim-svg]'),
    svgWrap: q('[data-sim-svg]'),
    presets: root.querySelectorAll('[data-preset]')
  };
  if (!el.heel || !el.svg || !window.ShoeProfile) return;

  // ---- geometry -----------------------------------------------------------
  // The shape now comes from assets/hub-shoe-profile.js (ShoeProfile.build).
  // The old anchor table lived here until 2026-08-25: an 18-point UPPER profile
  // hand-tuned against a photo, drawn at an arbitrary 1.9 units/mm. It produced a
  // rounded slab with a blunt toe. The replacement is millimetre-true against a
  // fixed 270mm US M9 last, with a real toe spring and a sculpted rocker.
  // git history holds the old table (1871182) if it is ever wanted back.
  //
  // DO NOT hand-edit hub-shoe-profile.js. Regenerate and re-vendor it instead.

  // PAINT ORDER, straight off the generator's spec. Later layers overlap earlier
  // ones. `many` = the key returns an array and needs one node each; `dots` = the
  // key returns {cx,cy,r} circles rather than path data.
  var LAYERS = [
    { k: 'grid',        a: { stroke: 'rgba(255,255,255,.05)', 'stroke-width': 0.4 } },
    { k: 'guides',      many: 1, a: { stroke: 'rgba(255,255,255,.07)', 'stroke-width': 0.4 } },
    { k: 'ground',      a: { stroke: 'rgba(255,255,255,.3)', 'stroke-width': 0.8 } },
    { k: 'limit',       a: { stroke: '#FBBF24', 'stroke-width': 0.5, 'stroke-dasharray': '3 3', opacity: 0.45 } },
    { k: 'midsole',     a: { fill: 'url(#simFoam)', stroke: '#8FCC00', 'stroke-width': 0.6 } },
    { k: 'sidewall',    many: 1, a: { stroke: 'rgba(11,14,18,.3)', 'stroke-width': 0.6 } },
    { k: 'outsole',     many: 1, a: { fill: '#0B0E12' } },
    { k: 'tread',       many: 1, a: { stroke: 'rgba(255,255,255,.16)', 'stroke-width': 0.6 } },
    { k: 'plate',       a: { fill: '#11151B' } },
    { k: 'tongue',      a: { fill: '#2A3542' } },
    { k: 'upper',       a: { fill: 'url(#simUpper)', stroke: 'rgba(255,255,255,.16)', 'stroke-width': 0.5 } },
    { k: 'toeCap',      a: { fill: '#46536A' } },
    { k: 'brandPanel',  a: { fill: '#46536A', opacity: 0.4 } },
    { k: 'heelCounter', a: { fill: '#47566A' } },
    { k: 'collarPad',   a: { fill: '#55647C' } },
    { k: 'counterSeam', a: { stroke: 'rgba(255,255,255,.2)', 'stroke-width': 0.4, 'stroke-dasharray': '2 2' } },
    { k: 'toeSeam',     a: { stroke: 'rgba(255,255,255,.2)', 'stroke-width': 0.4, 'stroke-dasharray': '2 2' } },
    { k: 'seams',       many: 1, a: { stroke: 'rgba(255,255,255,.18)', 'stroke-width': 0.4, 'stroke-dasharray': '2 2' } },
    { k: 'laces',       many: 1, a: { stroke: '#EFEFEF', 'stroke-width': 1.5, 'stroke-linecap': 'round' } },
    { k: 'eyelets',     many: 1, dots: 1, a: { fill: '#11151B', stroke: 'rgba(255,255,255,.5)', 'stroke-width': 0.4 } },
    // The foot runs up into the ankle and out of the frame, so it is masked to
    // fade before the top edge -- unmasked it reads as a dashed box floating
    // above the collar.
    { k: 'foot',        mask: 1, a: { stroke: 'rgba(255,255,255,.42)', 'stroke-width': 0.7, 'stroke-dasharray': '3 2' } },
    { k: 'loadPath',    a: { stroke: '#B7FF00', 'stroke-width': 0.9, opacity: 0.7 } },
    { k: 'loadArrow',   a: { stroke: '#B7FF00', 'stroke-width': 0.9, opacity: 0.7 } },
    { k: 'strikeArrow', a: { stroke: '#B7FF00', 'stroke-width': 0.9 } },
    { k: 'strikeMark',  dots: 1, a: { fill: '#B7FF00' } },
    { k: 'dims',        many: 1, a: { stroke: 'rgba(255,255,255,.26)', 'stroke-width': 0.4 } },
    { k: 'angles',      many: 1, a: { stroke: 'rgba(255,255,255,.26)', 'stroke-width': 0.4 } }
  ];

  var SVGNS = 'http://www.w3.org/2000/svg';
  function mk(tag, attrs) {
    var n = document.createElementNS(SVGNS, tag);
    for (var k in attrs) if (attrs.hasOwnProperty(k)) n.setAttribute(k, attrs[k]);
    return n;
  }

  // Nodes are built ONCE and only their `d` is updated afterwards. Every array
  // the generator returns has a fixed length across the whole input range
  // (checked: tread 16, laces 9, dims 14, eyelets 6, ...), so nothing needs
  // re-creating on a slider drag.
  var NODES = {}, LABELS = [];
  function buildLayers(model) {
    var frag = document.createDocumentFragment();
    LAYERS.forEach(function (L) {
      var host = frag;
      if (L.mask) { host = mk('g', { mask: 'url(#simShinFade)' }); frag.appendChild(host); }
      var v = model.paths[L.k];
      var n = L.many ? (Array.isArray(v) ? v.length : 0) : 1;
      var made = [];
      for (var i = 0; i < n; i++) {
        var node = mk(L.dots ? 'circle' : 'path', L.a);
        host.appendChild(node); made.push(node);
      }
      NODES[L.k] = made;
    });
    for (var j = 0; j < model.labels.length; j++) {
      var t = mk('text', { 'font-size': 7, 'font-family': 'ui-monospace, Menlo, monospace' });
      frag.appendChild(t); LABELS.push(t);
    }
    el.svg.appendChild(frag);
    el.svg.setAttribute('viewBox', model.viewBox);
  }

  var LABEL_FILL = { dim: 'rgba(239,239,239,.55)', accent: '#B7FF00', warn: '#FBBF24' };

  function paint(model) {
    LAYERS.forEach(function (L) {
      var v = model.paths[L.k], nodes = NODES[L.k] || [];
      var vals = L.many ? (Array.isArray(v) ? v : []) : [v];
      for (var i = 0; i < nodes.length; i++) {
        var d = vals[i];
        if (L.dots) {
          // plate:none returns null; a circle with r=0 is the cheapest "absent"
          nodes[i].setAttribute('cx', d ? d.cx : 0);
          nodes[i].setAttribute('cy', d ? d.cy : 0);
          nodes[i].setAttribute('r', d ? d.r : 0);
        } else {
          nodes[i].setAttribute('d', d || '');
        }
      }
    });
    for (var j = 0; j < LABELS.length; j++) {
      var l = model.labels[j];
      if (!l) { LABELS[j].textContent = ''; continue; }
      LABELS[j].setAttribute('x', l.x);
      LABELS[j].setAttribute('y', l.y);
      LABELS[j].setAttribute('text-anchor', l.anchor || 'start');
      LABELS[j].setAttribute('fill', LABEL_FILL[l.kind] || LABEL_FILL.dim);
      LABELS[j].textContent = l.text;
    }
  }

  // ---- readouts -----------------------------------------------------------
  function band(heel) {
    if (heel < 9) return 'Barefoot (under 9 mm)';
    if (heel < 20) return 'Minimal (9-20 mm)';
    if (heel < 35) return 'Regular (20-35 mm)';
    if (heel <= 40) return 'Maximalist (35 mm+)';
    return 'Maximalist, over the 40 mm road limit';
  }
  function legality(heel) {
    // World Athletics: 40mm for road, 25mm for track events 800m and up.
    if (heel > 40) return { t: 'Not legal for ranked road racing (over 40 mm)', k: 'bad' };
    if (heel > 25) return { t: 'Road-legal · over the 25 mm track limit', k: 'mid' };
    return { t: 'Legal for road and track', k: 'ok' };
  }
  function closest(heel, fore, weight) {
    return MATCHABLE.map(function (s) {
      return { s: s, d: Math.sqrt(Math.pow((s.heel - heel) / 40, 2) +
                                  Math.pow((s.fore - fore) / 40, 2) +
                                  Math.pow((s.weight - weight) / 200, 2)) };
    }).sort(function (a, b) { return a.d - b.d; }).slice(0, 4);
  }
  // ---- retail partner handoff -------------------------------------------------
  // The Lab already carries a map of our shoe id -> the partner's product handle
  // (assets/hub-partner-stock.js, 190 shoes) and the URL templates live on the
  // shoe-lab section's root. Reuse both rather than inventing a second source of
  // truth: a per-shoe product link beats a search, and a search beats nothing.
  var lab = document.querySelector('[data-shoelab]');
  var PARTNER = {
    name: (lab && lab.getAttribute('data-partner-name')) || '',
    product: (lab && lab.getAttribute('data-partner-product')) || '',
    search: (lab && lab.getAttribute('data-partner-search')) || ''
  };
  function partnerUrl(shoe) {
    var stock = window.HUB_PARTNER_STOCK || {};
    var handle = stock[shoe.id];
    if (handle && PARTNER.product) return PARTNER.product.replace('{handle}', encodeURIComponent(handle));
    if (PARTNER.search) return PARTNER.search.replace('{q}', encodeURIComponent(shoe.brand + ' ' + shoe.model));
    return '';
  }

  function esc(s) {
    return String(s == null ? '' : s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
  }

  // ---- render -------------------------------------------------------------
  // The running animation was removed 2026-08-24 at the owner's call: it never read as
  // a shoe running, only as a shape rotating, and a wrong-looking animation is worse
  // than none. The drawing is static and responds to the sliders. gait()/flexOffset()
  // and their tests went with it; git history has them if motion is revisited, but the
  // honest prerequisite is a designed shoe silhouette rather than procedural paths.

  var built = false;
  function draw() {
    var heel = +el.heel.value, fore = +el.fore.value, weight = +el.weight.value;
    if (fore > heel) { fore = heel; el.fore.value = heel; }

    var model = window.ShoeProfile.build({
      heelStack: heel, forefootStack: fore, weight: weight,
      plate: el.plate ? el.plate.value : 'none',
      strike: el.strike ? el.strike.value : 'mid'
    });
    if (!built) { buildLayers(model); built = true; }
    paint(model);

    el.heelOut.textContent = heel + ' mm';
    el.foreOut.textContent = fore + ' mm';
    el.weightOut.textContent = weight + ' g';
    el.drop.textContent = (heel - fore) + ' mm';
    el.band.textContent = band(heel);
    var lg = legality(heel);
    el.legal.textContent = lg.t;
    el.legal.className = 'sim__legal is-' + lg.k;

    lastMatches = closest(heel, fore, weight).map(function (m) { return m.s; });
    el.matches.innerHTML = lastMatches.map(function (x) {
      var url = partnerUrl(x);
      var spec = x.heel + ' / ' + x.fore + ' mm · ' + x.weight + ' g' + (x.cat ? ' · ' + esc(x.cat) : '');
      var inner = '<span class="sim__mname">' + esc(x.brand) + ' ' + esc(x.model) +
                  '</span><span class="sim__mspec">' + spec + '</span>';
      // rel="sponsored": these are outbound links to a commercial partner, and saying
      // so is both the honest signal and the one Google asks for.
      return url
        ? '<li class="sim__match"><a class="sim__mlink" href="' + esc(url) + '" rel="sponsored noopener" data-cursor>' +
          inner + '<span class="sim__mgo">Try at ' + esc(PARTNER.name || 'our partner') + ' &rarr;</span></a></li>'
        : '<li class="sim__match">' + inner + '</li>';
    }).join('');

  }

  /* Hand the current build to the comparison table at the top of the page. The
     closest match rides along so the new column has something to sit beside -
     a lone "Your build" column compares against nothing. */
  if (el.compare) {
    el.compare.addEventListener('click', function () {
      window.dispatchEvent(new CustomEvent('hub:build-compare', { detail: {
        heel: +el.heel.value, fore: +el.fore.value,
        weight: el.weight ? +el.weight.value : null,
        plate: el.plate ? el.plate.value : 'none',
        nearest: lastMatches[0] ? lastMatches[0].id : null
      } }));
    });
  }

  /* The comparison table can hand a build BACK when a shared ?compare= link
     carries one, so the bench and the table never show different shoes. */
  function applyRestore(d) {
    if (!d || !d.heel) return;
    el.heel.value = d.heel; el.fore.value = d.fore;
    if (el.weight && d.weight) el.weight.value = d.weight;
    if (el.plate && d.plate) el.plate.value = (d.plate === 'none' ? 'none' : 'carbon');
    draw();
  }
  window.addEventListener('hub:build-restore', function (e) { applyRestore(e && e.detail); });

  /* A #shoe-primer link must actually SHOW the primer. Linking to a closed
     <details> otherwise scrolls you to a heading and nothing else. */
  function openPrimerIfTargeted() {
    if (location.hash !== '#shoe-primer') return;
    var d = document.getElementById('shoe-primer');
    if (d && 'open' in d) { d.open = true; d.scrollIntoView({ block: 'start' }); }
  }
  window.addEventListener('hashchange', openPrimerIfTargeted);
  openPrimerIfTargeted();

  var hashTimer = null;
  function syncHash() {
    clearTimeout(hashTimer);
    // replaceState, not a hash assignment: dragging a slider must not fill the back
    // button with fifty history entries.
    hashTimer = setTimeout(function () {
      try { history.replaceState(null, '', '#' + buildHash()); } catch (e) {}
    }, 350);
  }
  ['input', 'change'].forEach(function (ev) {
    [el.heel, el.fore, el.weight, el.plate, el.strike].forEach(function (i) {
      if (i) i.addEventListener(ev, function () { draw(); syncHash(); });
    });
  });

  Array.prototype.forEach.call(el.presets, function (b) {
    b.addEventListener('click', function () {
      el.heel.value = b.getAttribute('data-heel');
      el.fore.value = b.getAttribute('data-fore');
      el.weight.value = b.getAttribute('data-weight');
      if (el.plate && b.getAttribute('data-plate')) el.plate.value = b.getAttribute('data-plate');
      Array.prototype.forEach.call(el.presets, function (o) {
        o.setAttribute('aria-pressed', o === b ? 'true' : 'false');
      });
      draw();
    });
  });

  // ---- shareable state ----------------------------------------------------------
  // The whole build lives in the URL, so a pasted link reopens the exact shoe. This is
  // what makes a stride card worth sharing: the card is the hook, the link is the tool.
  function readHash() {
    var m = (location.hash || '').match(/build=([^&]+)/);
    if (!m) return;
    var v = decodeURIComponent(m[1]).split(',');
    if (v[0]) el.heel.value = v[0];
    if (v[1]) el.fore.value = v[1];
    if (v[2]) el.weight.value = v[2];
    if (v[3] && el.plate) el.plate.value = v[3];
    if (v[4] && el.strike) el.strike.value = v[4];
  }
  function buildHash() {
    return 'build=' + [el.heel.value, el.fore.value, el.weight.value,
      el.plate ? el.plate.value : '', el.strike ? el.strike.value : ''].join(',');
  }
  function shareUrl() {
    return location.origin + location.pathname + '#' + buildHash();
  }

  if (el.share) {
    el.share.addEventListener('click', function () {
      var url = shareUrl();
      var title = 'I built a ' + el.heel.value + '/' + el.fore.value + ' mm shoe on the ATHLO+ Shoe Lab';
      if (navigator.share) {
        navigator.share({ title: title, text: title, url: url }).catch(function () {});
        return;
      }
      // No Web Share (most desktops): copy instead, and SAY that is what happened.
      var done = function (ok) {
        el.share.textContent = ok ? 'Link copied' : url;
        setTimeout(function () { el.share.textContent = 'Share this build'; }, 2600);
      };
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard.writeText(url).then(function () { done(true); }, function () { done(false); });
      } else { done(false); }
    });
  }

  // ---- the stride card ----------------------------------------------------------
  // Renders a 1200x630 PNG: the shoe as built, its numbers, and the real shoes it
  // landed on. 1200x630 because that is the OG card size, so the same image works as
  // a download AND as a social preview if it is ever uploaded as one.
  //
  // CO-BRANDED 2026-08-28 at the owner's ask: the ATHLO+ mark top-left, the iRUN mark
  // in the footer, and the matches headed "TRY THESE ON AT iRUN" instead of "CLOSEST
  // REAL SHOES". The list is unchanged — it is still the four nearest by spec — but the
  // heading now names the action rather than restating the algorithm.
  //
  // WHAT THE CARD IS ALLOWED TO CLAIM. `HUB_PARTNER_STOCK` maps our shoe id to a real
  // iRUN product handle, so "iRUN carries this" is checkable. It does NOT say which
  // branch has a pair in a box today, and no feed here does. So a stocked match gets a
  // dot and the footer says "stocked at iRUN — try them on in store"; a match we cannot
  // find in their catalogue gets nothing, and if NONE of them are stocked the store line
  // is omitted entirely rather than softened. A claim you cannot link does not get drawn.
  var CARD = {
    logo:        root.getAttribute('data-card-logo') || '',
    partnerLogo: root.getAttribute('data-card-partner-logo') || '',
    partnerName: root.getAttribute('data-card-partner-name') || 'iRUN',
    inStore:     root.getAttribute('data-card-instore') || 'TRY THESE ON AT iRUN',
    inStoreNote: root.getAttribute('data-card-instore-note') || 'Stocked at iRUN — try them on in store'
  };

  // Load an image for the canvas, or resolve null. crossOrigin='anonymous' is the whole
  // point: an image loaded WITHOUT it taints the canvas, and a tainted canvas makes
  // toBlob() throw SecurityError — so a decorative logo would silently kill the download
  // the card exists for. Resolving null on error means a missing logo costs a logo, not
  // the card. Shopify's CDN sends `access-control-allow-origin: *`, so this succeeds in
  // practice; the fallback path is for when it does not.
  function loadCors(src) {
    return new Promise(function (resolve) {
      if (!src) { resolve(null); return; }
      var im = new Image();
      im.crossOrigin = 'anonymous';
      im.onload = function () { resolve(im); };
      im.onerror = function () { resolve(null); };
      im.src = src;
    });
  }

  // The bundled iRUN mark is solid black on transparent — invisible on this card's
  // #0B0E12 ground. The site handles that with `filter: brightness(0) invert(1)`
  // (sections/hub-shoe-lab.liquid): force every pixel white, keep the alpha. Canvas 2D's
  // own `ctx.filter` would do it in one line but is missing on older Safari, so this does
  // the same thing by hand on an offscreen canvas. Safe because the source loaded CORS-clean.
  function whiten(img) {
    try {
      var c = document.createElement('canvas');
      c.width = img.naturalWidth || img.width;
      c.height = img.naturalHeight || img.height;
      var cx = c.getContext('2d');
      cx.drawImage(img, 0, 0);
      var d = cx.getImageData(0, 0, c.width, c.height);
      var px = d.data;
      for (var i = 0; i < px.length; i += 4) { px[i] = 255; px[i + 1] = 255; px[i + 2] = 255; }
      cx.putImageData(d, 0, 0);
      return c;
    } catch (e) {
      // getImageData throws only on a tainted canvas, which loadCors is meant to prevent.
      // Returning the untouched image keeps the card building; the mark just reads dark.
      return img;
    }
  }

  function drawCard() {
    var W = 1200, H = 630, cv = document.createElement('canvas');
    cv.width = W; cv.height = H;
    var g = cv.getContext('2d');
    g.fillStyle = '#0B0E12'; g.fillRect(0, 0, W, H);
    var glow = g.createRadialGradient(300, 60, 0, 300, 60, 700);
    glow.addColorStop(0, 'rgba(183,255,0,.13)'); glow.addColorStop(1, 'rgba(183,255,0,0)');
    g.fillStyle = glow; g.fillRect(0, 0, W, H);

    // The SVG carries its colours in a stylesheet, which does NOT travel with a
    // serialised clone — so every part gets explicit attributes before rasterising,
    // or the card comes out as black silhouettes.
    var svg = el.svg.cloneNode(true);
    // Inline every attribute before rasterising. A serialised clone does not
    // carry the stylesheet, and these are set as presentation attributes by the
    // renderer anyway -- but the gradients are document-scoped refs, so those two
    // fills have to be swapped for flat colours or the card comes out empty.
    Array.prototype.forEach.call(svg.querySelectorAll('[fill^="url("]'), function (n) {
      n.setAttribute('fill', n.getAttribute('fill').indexOf('simFoam') > -1 ? '#B7FF00' : '#333F4D');
    });
    var ser = new XMLSerializer().serializeToString(svg);
    var img = new Image();
    img.onload = function () {
      // The two logos are optional and independent. Whichever resolves gets drawn; a null
      // falls back to type, so the card is co-branded when it can be and still correct when
      // it cannot. Promise.all never rejects here — loadCors resolves null on error.
      Promise.all([loadCors(CARD.logo), loadCors(CARD.partnerLogo)]).then(function (logos) {
        finish(logos[0], logos[1]);
      });

      function finish(athloLogo, partnerLogo) {
      g.drawImage(img, 40, 150, 700, 452);

      // ---- masthead: the ATHLO+ mark, or its wordmark if the image did not load ----
      if (athloLogo) {
        var lw = athloLogo.naturalWidth || athloLogo.width;
        var lh = athloLogo.naturalHeight || athloLogo.height;
        var h = 46, w = lh ? (lw / lh) * h : h;
        g.drawImage(athloLogo, 60, 44, w, h);
        g.fillStyle = '#B7FF00';
        g.font = '700 22px Helvetica, Arial, sans-serif';
        g.fillText('SHOE LAB', 60 + w + 18, 78);
      } else {
        g.fillStyle = '#B7FF00';
        g.font = '700 26px Helvetica, Arial, sans-serif';
        g.fillText('ATHLO+ SHOE LAB', 60, 78);
      }
      g.fillStyle = '#EFEFEF';
      g.font = '700 62px Helvetica, Arial, sans-serif';
      g.fillText('I built this shoe.', 60, 140);

      var x = 760, y = 190;
      var stat = function (k, v) {
        g.fillStyle = 'rgba(239,239,239,.6)';
        g.font = '600 17px Helvetica, Arial, sans-serif';
        g.fillText(k.toUpperCase(), x, y);
        g.fillStyle = '#EFEFEF';
        g.font = '700 40px Helvetica, Arial, sans-serif';
        g.fillText(v, x, y + 44);
        y += 86;
      };
      stat('Heel / forefoot', el.heel.value + ' / ' + el.fore.value + ' mm');
      stat('Drop', (el.heel.value - el.fore.value) + ' mm');
      stat('Weight', el.weight.value + ' g');

      // ---- the matches, headed by what to DO with them ----
      var stock = window.HUB_PARTNER_STOCK || {};
      var shown = lastMatches.slice(0, 3);
      var anyStocked = shown.some(function (m) { return !!stock[m.id]; });

      g.fillStyle = 'rgba(239,239,239,.6)';
      g.font = '600 17px Helvetica, Arial, sans-serif';
      g.fillText(String(CARD.inStore).toUpperCase(), x, y + 6);
      shown.forEach(function (m, i) {
        var ly = y + 42 + i * 32;
        // A lime dot means "iRUN lists this product" — the one thing the stock map can
        // actually prove. No dot is not "out of stock", it is "we have no link for it",
        // which is why nothing on the card says out of stock.
        if (stock[m.id]) {
          g.fillStyle = '#B7FF00';
          g.beginPath(); g.arc(x + 5, ly - 7, 5, 0, Math.PI * 2); g.fill();
        }
        g.fillStyle = '#EFEFEF';
        g.font = '400 21px Helvetica, Arial, sans-serif';
        g.fillText(m.brand + ' ' + m.model, x + 20, ly);
      });

      // ---- footer: athlo.sg on the left, the partner lockup on the right ----
      g.fillStyle = 'rgba(239,239,239,.45)';
      g.font = '400 19px Helvetica, Arial, sans-serif';
      g.fillText('athlo.sg  ·  build your own', 60, H - 42);

      if (anyStocked && CARD.inStoreNote) {
        var px2 = W - 60, py = H - 52;
        if (partnerLogo) {
          var src = whiten(partnerLogo);
          var pw = src.width || partnerLogo.naturalWidth;
          var ph = src.height || partnerLogo.naturalHeight;
          var ph2 = 34, pw2 = ph ? (pw / ph) * ph2 : ph2;
          px2 -= pw2;
          g.globalAlpha = 0.92;
          g.drawImage(src, px2, py - 4, pw2, ph2);
          g.globalAlpha = 1;
          px2 -= 16;
        }
        g.fillStyle = 'rgba(239,239,239,.55)';
        g.font = '400 17px Helvetica, Arial, sans-serif';
        g.textAlign = 'right';
        g.fillText(CARD.inStoreNote, px2, H - 42);
        g.textAlign = 'left';
      }

      cv.toBlob(function (blob) {
        if (!blob) return;
        var a = document.createElement('a');
        a.href = URL.createObjectURL(blob);
        a.download = 'athlo-shoe-' + el.heel.value + '-' + el.fore.value + '.png';
        a.click();
        setTimeout(function () { URL.revokeObjectURL(a.href); }, 4000);
      }, 'image/png');
      }
    };
    img.onerror = function () {
      if (el.card) { el.card.textContent = 'Could not build the card'; }
    };
    img.src = 'data:image/svg+xml;charset=utf-8,' + encodeURIComponent(ser);
  }
  if (el.card) el.card.addEventListener('click', function () {
    el.card.textContent = 'Building…';
    drawCard();
    setTimeout(function () { el.card.textContent = 'Download stride card'; }, 1800);
  });

  readHash();
  if (window.HUB_PENDING_BUILD && (location.hash || '').indexOf('build=') < 0) {
    applyRestore(window.HUB_PENDING_BUILD);
  }
  if (el.count) el.count.textContent = MATCHABLE.length;
  draw();
  window.addEventListener('hashchange', function () { readHash(); draw(); });
})();
